import NoDataIcon from '@assets/no-data.svg?react';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useIsNoComms from '@src/hooks/useIsNoComms';
import { LockedDeviceInfo } from '@src/types/ControlLockMode';
import { SiteMeta } from '@src/types/SiteMeta';
import storeConnector from '@store/storeConnector';
import { MqttClient } from 'mqtt';
import { ReactElement } from 'react';

interface IconProps {
  isControllerLocked: boolean;
  isNoComms: boolean;
  circleClass: string;
}

function Icon(p: IconProps): ReactElement {
  if (p.isControllerLocked) {
    return (
      <div className='pointer'>
        <FontAwesomeIcon style={{ cursor: 'pointer' }} icon={faLock} />
      </div>
    );
  }

  if (p.isNoComms) {
    return (
      <div role='no-data-icon'>
        <NoDataIcon />
      </div>
    );
  }

  return <i role='status-circle' className={`status ${p.circleClass}`} />;
}

interface TooltipProps {
  invStatus: string;
  runMode: string;
  allowedMinP: number;
  allowedMaxP: number;
  allowedMinQ: number;
  allowedMaxQ: number;
  pcsP: number;
  pcsQ: number;
}

interface TooltipLockedProps {
  updatedBy: string;
  updatedAt: string;
  note: string;
}

function Tooltip(p: TooltipProps): ReactElement {
  return (
    <div className='loto-mode-tooltip'>
      <div className='flex-row-s'>
        Mode: {p.invStatus} / {p.runMode}
      </div>
      <div className='flex-row-s'>
        P-limit:&nbsp;
        {p.allowedMinP} / {p.allowedMaxP}
      </div>
      <div className='flex-row-s'>
        Q-Limit:&nbsp;
        {p.allowedMinQ} / {p.allowedMaxQ}
      </div>
      <div className='flex-row-s'>
        kW Cmd:&nbsp;
        {p.pcsP}
      </div>
      <div className='flex-row-s'>
        kVAR Cmd:&nbsp;
        {p.pcsQ}
      </div>
    </div>
  );
}

function TooltipLocked(p: TooltipLockedProps): ReactElement {
  return (
    <div className='loto-mode-tooltip'>
      <div className='flex-row-s'>Locked By: {p.updatedBy}</div>
      <div className='flex-row-s'>Locked On: {p.updatedAt}</div>
      <div className='flex-row-s'>Note: {p.note}</div>
    </div>
  );
}

export interface ModeDataCellProps {
  unitSN: string;
  circleClass: string;
  invStatus: string;
  runMode: string;
  allowedMinP: number;
  allowedMaxP: number;
  allowedMinQ: number;
  allowedMaxQ: number;
  pcsP: number;
  pcsQ: number;
  siteMeta: SiteMeta;
  useIsNoCommsMqttClient: MqttClient;
  lockedControllers: LockedDeviceInfo[];
}

function ModeDataCell(p: ModeDataCellProps) {
  const { dataRateTimeoutSeconds } = p.siteMeta.ui;
  const { siteId } = p.siteMeta;
  const { useIsNoCommsMqttClient, unitSN, lockedControllers } = p;

  const isNoComms = useIsNoComms(
    siteId,
    dataRateTimeoutSeconds,
    useIsNoCommsMqttClient,
    unitSN,
  );

  const isControllerLocked = lockedControllers
    .map((device) => device.deviceId)
    .includes(unitSN);

  return (
    <td className={`${isControllerLocked && 'red-text'} loto-mode-box`}>
      <Icon
        isControllerLocked={isControllerLocked}
        isNoComms={isNoComms}
        circleClass={p.circleClass}
      />

      {!isControllerLocked && (
        <Tooltip
          invStatus={p.invStatus}
          runMode={p.runMode}
          allowedMinP={p.allowedMinP}
          allowedMaxP={p.allowedMaxP}
          allowedMinQ={p.allowedMinQ}
          allowedMaxQ={p.allowedMaxQ}
          pcsP={p.pcsP}
          pcsQ={p.pcsQ}
        />
      )}

      {isControllerLocked && (
        <TooltipLocked
          updatedBy={
            lockedControllers.find((device) => device.deviceId === unitSN)
              ?.updatedBy || ''
          }
          updatedAt={
            lockedControllers.find((device) => device.deviceId === unitSN)
              ?.updatedAt || ''
          }
          note={
            lockedControllers.find((device) => device.deviceId === unitSN)
              ?.note || ''
          }
        />
      )}
    </td>
  );
}

export default storeConnector(ModeDataCell, {
  controlsLock: ['lockedControllers'],
});
